// Provenance: repo:tds-reconciliation-portal:frontend/src/components/ReconcileButton.tsx
// (ADR-0005) — restyled (ADR-0010). Run ids became UUID strings, shown by their 8-hex tag.
import { Play } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError, postJson } from '../api/client'
import type { Run } from '../api/types'
import { runTag } from '../format'
import { Btn, Modal } from './Modal'
import { showToast } from './toast'

/** Reconcile again, with the aliases approved now.
 *
 *  This replaces Refresh. Refresh re-read the screen and deliberately never
 *  re-ran — honest, and the wrong answer to the question being asked. The order
 *  of work is reconcile, read the names, set the aliases, reconcile again, and
 *  there was no "again" on the screen where the aliasing happens.
 *
 *  Removing Refresh must not cost a reviewer anything, so the page keeps itself
 *  current instead (`refetchOnWindowFocus` on the reconciliation queries). That
 *  is behaviour, not a second button.
 */
export function ReconcileButton({
  runId,
  aliasesSinceRun,
  onStarted,
}: {
  runId: string
  /** approved alias groups this run does not have — empty means a re-run would
   *  produce an identical answer, and the user should be told before spending
   *  the engine time */
  aliasesSinceRun: string[]
  onStarted: (run: Run) => void
}) {
  const queryClient = useQueryClient()
  const [confirm, setConfirm] = useState(false)
  const pending = aliasesSinceRun.length

  const start = useMutation({
    mutationFn: () => postJson<Run>(`/runs/${runId}/rerun`, {}),
    onSuccess: (run) => {
      setConfirm(false)
      void queryClient.invalidateQueries({ queryKey: ['runs'] })
      showToast({
        color: 'blue',
        title: `Run ${runTag(run.id)} started`,
        message: 'Your matches and explanations carry over — they are keyed to '
          + 'the entries, not to the run.',
      })
      onStarted(run)
    },
    onError: (e) =>
      showToast({
        color: 'red',
        title: 'Could not start the run',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })

  const go = () => (pending ? start.mutate() : setConfirm(true))

  return (
    <>
      <Btn
        size="xs"
        kind={pending ? 'primary' : 'default'}
        loading={start.isPending}
        onClick={go}
        title={
          pending
            ? `Reconcile again with ${pending} alias group(s) approved since this run`
            : 'Reconcile again — nothing has changed since this run'
        }
      >
        <Play size={14} /> Reconcile{pending ? ` (${pending} new)` : ''}
      </Btn>

      {/* Nothing has changed, so the result would be identical. Said before the
          engine time is spent rather than discovered after it. */}
      <Modal
        opened={confirm}
        onClose={() => setConfirm(false)}
        title="Nothing has changed since this run"
      >
        <div className="space-y-3">
          <p className="text-sm text-foreground">
            No alias group has been approved since run {runTag(runId)} was created, so
            reconciling again would produce the same figures. Set an alias on
            the Alias &amp; Rules screen first, or run it anyway.
          </p>
          <p className="text-xs text-muted-foreground">
            Either way your matches and explanations carry over — they are keyed
            to the entries, not to the run — and run {runTag(runId)} stays openable.
          </p>
          <Btn size="xs" loading={start.isPending} onClick={() => start.mutate()}>
            Run it anyway
          </Btn>
        </div>
      </Modal>
    </>
  )
}
